<template>
  <div class="repair-page">
    <el-card class="box-card" shadow="never">
      <div slot="header" class="clearfix header-bar">
        <span class="page-title">故障报修</span>
        <div class="header-actions">
          <el-button size="small" icon="el-icon-download" :loading="exporting" @click="handleExportExcel">导出 Excel</el-button>
          <el-button size="small" type="danger" icon="el-icon-lightning" @click="openBatchFlag">⚡ 急救设备批量打标</el-button>
          <el-button size="small" icon="el-icon-refresh-right" @click="handleReset">重置</el-button>
        </div>
      </div>

      <!-- 查询区：label 13px、控件 size=small、高度 32px（与设备台账/安全阀统一） -->
      <el-form :inline="true" :model="queryForm" size="small" class="search-form" @submit.native.prevent>
        <el-form-item label="工单状态">
          <el-select v-model="queryForm.status" placeholder="全部" clearable style="width: 130px">
            <el-option v-for="s in statusOptions" :key="s.value" :label="s.label" :value="s.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="科室">
          <department-select v-model="queryForm.department" placeholder="全部科室" clearable style="width: 140px" />
        </el-form-item>
        <el-form-item label="关键字">
          <el-input v-model="queryForm.keyword" placeholder="工单号/设备名/编号/故障描述" clearable style="width: 220px" />
        </el-form-item>
        <el-form-item label="接单人">
          <el-input v-model="queryForm.accept_by" placeholder="接单人姓名" clearable style="width: 120px" />
        </el-form-item>
        <el-form-item label="急救标">
          <el-select v-model="queryForm.is_emergency" placeholder="全部" clearable style="width: 110px">
            <el-option label="仅急救/生命支持" value="true" />
            <el-option label="非急救" value="false" />
          </el-select>
        </el-form-item>
        <el-form-item label="报修日期">
          <el-date-picker
            v-model="queryForm.dateRange"
            type="daterange"
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            clearable
            style="width: 250px"
          />
        </el-form-item>
        <el-form-item class="search-buttons">
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
        </el-form-item>
      </el-form>

      <el-alert
        type="info"
        :closable="false"
        show-icon
        class="legend-alert"
        title="行底色＝优先级：淡红＝急救/生命支持类设备，淡橙＝被回退过的单（加急），两者同时满足显示红色。点击任意行查看完整维修记录单。"
      />

      <el-table
        v-loading="loading"
        :data="list"
        row-key="order_no"
        border
        size="small"
        class="repair-table"
        :row-class-name="rowClassName"
        @row-click="onRowClick"
        @selection-change="onSelectionChange"
      >
        <el-table-column type="selection" width="42" align="center" reserve-selection />
        <el-table-column type="index" :index="mainIndex" label="序号" width="55" align="center" />
        <el-table-column prop="order_no" label="工单号" width="150" show-overflow-tooltip />
        <el-table-column prop="equipment_name" label="设备名称" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">
            <span>{{ scope.row.equipment_name || '-' }}</span>
            <el-tag v-if="scope.row.is_emergency" type="danger" size="mini" class="mark-tag">急救</el-tag>
            <el-tag v-else-if="scope.row.is_urgent" type="warning" size="mini" class="mark-tag">加急</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="equipment_model" label="型号" width="120" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.equipment_model || '-' }}</template>
        </el-table-column>
        <el-table-column prop="department" label="科室" width="110" show-overflow-tooltip />
        <el-table-column prop="reporter_name" label="报修人" width="90" />
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="statusTagType(scope.row.status)" size="small">{{ statusText(scope.row) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="接单人" min-width="150">
          <template slot-scope="scope">
            <span>{{ scope.row.accept_by_name || '— 无人接单 —' }}</span>
            <!-- 未接单且已超时：红字提示等待时长，便于人工派单 -->
            <span v-if="scope.row.status === 'PENDING' && scope.row.overdue" class="overdue-text">
              ⏰ 已等待 {{ scope.row.waiting_minutes }} 分钟
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="accepted_at" label="接单时间" width="150" align="center">
          <template slot-scope="scope">{{ scope.row.accepted_at || '-' }}</template>
        </el-table-column>
        <el-table-column label="回退次数" width="80" align="center">
          <template slot-scope="scope">
            <span :class="{ 'return-count': scope.row.return_count > 0 }">{{ scope.row.return_count || 0 }}</span>
          </template>
        </el-table-column>
        <el-table-column label="维修结果" width="110" align="center">
          <template slot-scope="scope">{{ repairResultText(scope.row) }}</template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="150" align="center" />
        <el-table-column label="操作" width="120" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click.stop="openDetail(scope.row)">详情</el-button>
            <el-button
              v-if="scope.row.status === 'PENDING'"
              type="text"
              class="dispatch-text"
              @click.stop="openDispatch(scope.row)"
            >派单</el-button>
          </template>
        </el-table-column>
        <template slot="empty">
          <span v-if="backendError">后端未启动或接口异常，请检查服务</span>
          <span v-else>暂无报修工单</span>
        </template>
      </el-table>

      <el-pagination
        class="pager"
        background
        layout="total, sizes, prev, pager, next"
        :total="total"
        :page-size="pageSize"
        :page-sizes="[10, 20, 50]"
        :current-page.sync="currentPage"
        @size-change="handleSizeChange"
        @current-change="fetchList"
      />
    </el-card>

    <!-- 工单详情：按法定维修记录单的 5 个区分块排版 -->
    <el-dialog
      title="维修记录单（工单详情）"
      :visible.sync="detailVisible"
      width="900px"
      top="5vh"
      :close-on-click-modal="false"
      @closed="detail = null"
    >
      <div v-if="detail" v-loading="detailLoading" class="detail-wrap">
        <div class="detail-head">
          <span class="detail-order">工单号：{{ detail.order_no }}</span>
          <el-tag :type="statusTagType(detail.status)" size="small">{{ statusText(detail) }}</el-tag>
          <el-tag v-if="detail.is_emergency" type="danger" size="small">急救/生命支持类</el-tag>
          <el-tag v-else-if="detail.is_urgent" type="warning" size="small">回退加急</el-tag>
        </div>

        <!-- ===== 区块 1：设备档案 ===== -->
        <el-divider content-position="left">一、设备档案</el-divider>
        <el-row :gutter="10" class="detail-row">
          <el-col :span="8"><span class="k">设备名称：</span>{{ detail.equipment_name || '-' }}</el-col>
          <el-col :span="8"><span class="k">型号：</span>{{ detail.equipment_model || '-' }}</el-col>
          <el-col :span="8"><span class="k">出厂编号：</span>{{ detail.equipment_factory_no || '-' }}</el-col>
          <el-col :span="8"><span class="k">生产企业：</span>{{ detail.equipment_manufacturer || '-' }}</el-col>
          <el-col :span="8"><span class="k">使用科室：</span>{{ detail.department || '-' }}</el-col>
          <el-col :span="8"><span class="k">设备编号：</span>{{ detail.equipment_id || '-' }}</el-col>
          <el-col :span="24"><span class="k">安装位置：</span>{{ detail.location_note || '-' }}</el-col>
        </el-row>

        <!-- ===== 区块 2：报修 ===== -->
        <el-divider content-position="left">二、报修信息</el-divider>
        <el-row :gutter="10" class="detail-row">
          <el-col :span="8"><span class="k">报修人：</span>{{ detail.reporter_name || '-' }}</el-col>
          <el-col :span="8"><span class="k">报修科室：</span>{{ detail.reporter_dept || '-' }}</el-col>
          <el-col :span="8"><span class="k">报修时间：</span>{{ detail.created_at || '-' }}</el-col>
          <el-col :span="24"><span class="k">故障描述：</span>{{ detail.fault_desc || '-' }}</el-col>
          <el-col :span="24">
            <span class="k">故障标签：</span>
            <el-tag v-for="t in tagList(detail.fault_tags)" :key="t" size="mini" class="tag-gap">{{ t }}</el-tag>
            <span v-if="!tagList(detail.fault_tags).length">-</span>
          </el-col>
        </el-row>

        <!-- ===== 区块 3：维修 ===== -->
        <el-divider content-position="left">三、维修信息</el-divider>
        <el-row :gutter="10" class="detail-row">
          <el-col :span="8"><span class="k">接单人：</span>{{ detail.accept_by_name || '-' }}</el-col>
          <el-col :span="8"><span class="k">接单来源：</span>{{ acceptSourceText(detail) }}</el-col>
          <el-col :span="8"><span class="k">接单时间：</span>{{ detail.accepted_at || '-' }}</el-col>
          <el-col :span="8"><span class="k">到场时间：</span>{{ detail.arrived_at || '-' }}</el-col>
          <el-col :span="8"><span class="k">开工时间：</span>{{ detail.started_at || '-' }}</el-col>
          <el-col :span="8"><span class="k">完毕日期：</span>{{ detail.finished_at || '-' }}</el-col>
          <el-col :span="8"><span class="k">维修单位：</span>{{ repairUnitText(detail) }}</el-col>
          <el-col :span="8"><span class="k">维修人：</span>{{ detail.repair_person || '-' }}</el-col>
          <el-col :span="8"><span class="k">保修状态：</span>{{ warrantyText(detail) }}</el-col>
          <el-col :span="12"><span class="k">维修结果：</span>{{ repairResultText(detail) }}</el-col>
          <el-col :span="12"><span class="k">回退次数：</span>{{ detail.return_count || 0 }}</el-col>
          <el-col :span="24"><span class="k">故障原因：</span>{{ detail.fault_cause || '-' }}</el-col>
          <el-col :span="24"><span class="k">维修经过：</span>{{ detail.finish_desc || '-' }}</el-col>
          <el-col :span="12"><span class="k">挂起原因：</span>{{ detail.suspend_reason || '-' }}</el-col>
          <el-col :span="12"><span class="k">预计恢复：</span>{{ detail.suspend_expected || '-' }}</el-col>
        </el-row>

        <!-- ===== 区块 4：更换配件 ===== -->
        <el-divider content-position="left">四、更换配件</el-divider>
        <el-table :data="detail.parts || []" border size="small" empty-text="本次维修未更换配件">
          <el-table-column type="index" label="序号" width="55" align="center" />
          <el-table-column prop="part_name" label="配件名称" min-width="130" show-overflow-tooltip />
          <el-table-column prop="part_model" label="型号" width="120" show-overflow-tooltip />
          <el-table-column prop="quantity" label="数量" width="70" align="center" />
          <el-table-column prop="manufacturer" label="生产企业" min-width="140" show-overflow-tooltip />
          <el-table-column prop="unit_price" label="单价(元)" width="90" align="right" />
          <el-table-column prop="amount" label="金额(元)" width="90" align="right" />
        </el-table>
        <div v-if="(detail.parts || []).length" class="parts-total">配件合计金额：{{ partsTotal(detail) }} 元</div>

        <!-- ===== 区块 5：验证与验收 ===== -->
        <el-divider content-position="left">五、验证与验收</el-divider>
        <el-row :gutter="10" class="detail-row">
          <el-col :span="24"><span class="k">修复后检测：</span>{{ detail.test_result || '-' }}</el-col>
          <el-col :span="8"><span class="k">验收人：</span>{{ detail.signed_by_name || '-' }}</el-col>
          <el-col :span="8"><span class="k">验收时间：</span>{{ detail.signed_at || '-' }}</el-col>
          <el-col :span="8"><span class="k">追加记录：</span>{{ detail.append_count || 0 }} 次</el-col>
          <el-col :span="24"><span class="k">最近回退原因：</span>{{ detail.last_return_reason || '-' }}</el-col>
        </el-row>

        <!-- ===== 全流程时间线 ===== -->
        <el-divider content-position="left">全流程时间线</el-divider>
        <el-timeline class="detail-timeline">
          <el-timeline-item
            v-for="(node, i) in timelineNodes"
            :key="i"
            :timestamp="node.time"
            :type="node.type"
            :color="node.color"
            :icon="node.icon"
            size="normal"
            placement="top"
          >
            <div class="tl-title">{{ node.title }}</div>
            <div v-if="node.desc" class="tl-desc">{{ node.desc }}</div>
          </el-timeline-item>
        </el-timeline>

        <!-- ===== 附件区：图片看大图、语音可播放 ===== -->
        <el-divider content-position="left">附件（{{ (detail.attachments || []).length }}）</el-divider>
        <div v-if="(detail.attachments || []).length" class="attach-wrap">
          <div v-for="(a, i) in detail.attachments" :key="i" class="attach-item">
            <el-image
              v-if="a.kind === 'image'"
              :src="mediaUrl(a.url)"
              :preview-src-list="imageList"
              fit="cover"
              class="attach-image"
            />
            <audio v-else-if="a.kind === 'voice' || a.kind === 'audio'" :src="mediaUrl(a.url)" controls class="attach-audio" />
            <a v-else :href="mediaUrl(a.url)" target="_blank" class="attach-link">附件 {{ i + 1 }}（{{ a.kind || '文件' }}）</a>
          </div>
        </div>
        <div v-else class="attach-empty">暂无附件</div>
      </div>
      <span slot="footer">
        <!-- 导出维修记录单 PDF：等级评审/飞检要的纸面记录，后端 repair/report.py 法定 5 区排版 -->
        <el-button
          v-if="detail"
          type="primary"
          size="small"
          icon="el-icon-download"
          :loading="reportLoading"
          @click="exportReport"
        >导出维修记录单(PDF)</el-button>
        <el-button @click="detailVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 派单弹窗：仅 PENDING 工单 -->
    <el-dialog title="指派后勤人员" :visible.sync="dispatchVisible" width="460px" :close-on-click-modal="false">
      <el-alert
        type="info"
        :closable="false"
        show-icon
        :title="`工单 ${dispatchOrderNo} 当前无人接单，可直接指派给后勤人员。`"
      />
      <el-form label-width="90px" class="dispatch-form">
        <el-form-item label="后勤人员">
          <el-select
            v-model="dispatchOpenid"
            filterable
            placeholder="请选择后勤人员"
            :loading="staffLoading"
            style="width: 100%"
          >
            <el-option
              v-for="s in staffList"
              :key="s.openid"
              :value="s.openid"
              :label="s.name + (s.dept ? '（' + s.dept + '）' : '')"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dispatchVisible = false">取消</el-button>
        <el-button type="primary" :loading="dispatching" @click="submitDispatch">确认派单</el-button>
      </span>
    </el-dialog>

    <!-- 急救/生命支持类设备批量打标 -->
    <el-dialog title="⚡ 急救/生命支持类设备批量打标" :visible.sync="flagVisible" width="760px" :close-on-click-modal="false">
      <el-alert
        type="warning"
        :closable="false"
        show-icon
        title="按设备名称关键字匹配台账设备，先预览再执行。评审 ★6.4.4 要求急救/生命支持类设备有监管记录，打标后此类工单会在报修列表红底显示并可单独导出成册。"
      />
      <el-form label-width="90px" class="flag-form">
        <el-form-item label="名称关键字">
          <el-input
            v-model="flagKeywords"
            type="textarea"
            :rows="2"
            placeholder="多个关键字用逗号分隔"
          />
        </el-form-item>
        <el-form-item>
          <el-button icon="el-icon-search" :loading="flagLoading" @click="previewFlag">预览匹配设备</el-button>
          <el-button
            type="danger"
            icon="el-icon-check"
            :loading="flagSubmitting"
            :disabled="flagRows.length === 0"
            @click="submitFlag"
          >确认打标（{{ flagPendingCount }} 台）</el-button>
        </el-form-item>
      </el-form>

      <el-table :data="flagRows" v-loading="flagLoading" border size="small" height="320" empty-text="请先预览匹配设备">
        <el-table-column type="index" label="序号" width="55" align="center" />
        <el-table-column prop="name" label="设备名称" min-width="150" show-overflow-tooltip />
        <el-table-column prop="model" label="型号" width="120" show-overflow-tooltip />
        <el-table-column prop="department" label="科室" width="110" show-overflow-tooltip />
        <el-table-column label="当前状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.is_life_support ? 'danger' : 'info'" size="small">
              {{ scope.row.is_life_support ? '已打标' : '未打标' }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer">
        <el-button @click="flagVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import repairApi from '../api/repair'
import equipmentApi from '../api/equipment'
import DepartmentSelect from './DepartmentSelect.vue'

// 工单状态枚举（与后端保持一致）
const STATUS_OPTIONS = [
  { value: 'PENDING', label: '待接单' },
  { value: 'PROCESSING', label: '维修中' },
  { value: 'SUSPENDED', label: '挂起' },
  { value: 'WAIT_SIGN', label: '待签字' },
  { value: 'SIGNED', label: '已关闭' },
  { value: 'CANCELLED', label: '已撤销' },
]
const STATUS_TEXT = {
  PENDING: '待接单',
  PROCESSING: '维修中',
  SUSPENDED: '挂起',
  WAIT_SIGN: '待签字',
  SIGNED: '已关闭',
  CANCELLED: '已撤销',
}
const RESULT_TEXT = {
  fixed: '修好',
  temporary: '临时处理',
  scrap_eval: '需报废评估',
  vendor: '转厂商',
  unfixed: '未修复',
}
const REPAIR_UNIT_TEXT = { hospital: '本院', vendor: '厂商', third: '第三方' }
const WARRANTY_TEXT = { in_warranty: '保修期内', expired: '过保', contract: '合同维保' }
const ACCEPT_SOURCE_TEXT = { scan: '扫码接单', pool: '接单池', dispatch: '后台派单' }

export default {
  name: 'RepairList',
  components: { DepartmentSelect },
  data() {
    return {
      list: [],
      total: 0,
      selectedRows: [], // 勾选的工单（导出指定工单用）
      currentPage: 1,
      pageSize: 10,
      loading: false,
      exporting: false,
      backendError: false,
      statusOptions: STATUS_OPTIONS,
      queryForm: {
        status: '',
        department: '',
        keyword: '',
        accept_by: '',
        is_emergency: '',
        dateRange: ['', ''],
      },
      // 详情弹窗
      detailVisible: false,
      detailLoading: false,
      detail: null,
      reportLoading: false,
      // 派单弹窗
      dispatchVisible: false,
      dispatchOrderNo: '',
      dispatchOpenid: '',
      dispatchLoading: false,
      dispatching: false,
      staffLoading: false,
      staffList: [],
      // 批量打标弹窗
      flagVisible: false,
      flagKeywords: '呼吸机,麻醉机,除颤仪,监护仪,心电图机,输液泵,注射泵,高流量,抢救车,负压吸引器',
      flagLoading: false,
      flagSubmitting: false,
      flagRows: [],
    }
  },
  computed: {
    // 时间线节点：报修 → 接单 → 到场 → 开工 → [挂起/回退] → 完工 → 签字
    timelineNodes() {
      const d = this.detail
      if (!d) return []
      const nodes = []
      nodes.push({
        title: '报修',
        time: d.created_at || '',
        desc: `${d.reporter_name || '-'}（${d.reporter_dept || '-'}）报修：${d.fault_desc || ''}`,
        type: 'primary',
        icon: 'el-icon-phone-outline',
      })
      if (d.accepted_at) {
        nodes.push({
          title: '接单',
          time: d.accepted_at,
          desc: `${d.accept_by_name || '-'} 接单（${this.acceptSourceText(d)}）`,
          type: 'primary',
          icon: 'el-icon-user',
        })
      }
      if (d.arrived_at) {
        nodes.push({ title: '到场', time: d.arrived_at, desc: '维修人员到达现场', type: 'primary', icon: 'el-icon-location-outline' })
      }
      if (d.started_at) {
        nodes.push({ title: '开工维修', time: d.started_at, desc: d.fault_cause ? '故障原因：' + d.fault_cause : '', type: 'primary', icon: 'el-icon-setting' })
      }
      if (d.suspend_reason) {
        nodes.push({
          title: '挂起',
          time: d.suspend_expected ? '预计恢复：' + d.suspend_expected : '',
          desc: d.suspend_reason,
          type: 'warning',
          icon: 'el-icon-time',
        })
      }
      if (d.return_count > 0) {
        nodes.push({
          title: `验收回退（${d.return_count} 次）`,
          time: '',
          desc: d.last_return_reason || '科室验收不通过，退回重修',
          type: 'danger',
          icon: 'el-icon-refresh-left',
        })
      }
      if (d.finished_at) {
        nodes.push({
          title: '完工',
          time: d.finished_at,
          desc: `${this.repairResultText(d)}｜${this.repairUnitText(d)}${d.repair_person ? '／' + d.repair_person : ''}；${d.finish_desc || ''}`,
          type: 'success',
          icon: 'el-icon-circle-check',
        })
      }
      if (d.signed_at) {
        nodes.push({
          title: '科室签字验收',
          time: d.signed_at,
          desc: `${d.signed_by_name || '-'} 签字确认；${d.test_result ? '修复后检测：' + d.test_result : ''}`,
          type: 'success',
          icon: 'el-icon-edit-outline',
        })
      }
      return nodes
    },
    // 详情大图预览列表（只取图片类附件）
    imageList() {
      return (this.detail && this.detail.attachments ? this.detail.attachments : [])
        .filter(a => a.kind === 'image')
        .map(a => this.mediaUrl(a.url))
    },
    // 打标预览中尚未打标的台数
    flagPendingCount() {
      return this.flagRows.filter(r => !r.is_life_support).length
    },
  },
  created() {
    this.fetchList()
  },
  methods: {
    // ---------- 列表 ----------
    buildParams() {
      const p = { page: this.currentPage, page_size: this.pageSize }
      const q = this.queryForm
      if (q.status) p.status = q.status
      if (q.department) p.department = q.department
      if (q.keyword) p.keyword = q.keyword
      if (q.accept_by) p.accept_by = q.accept_by
      if (q.is_emergency) p.is_emergency = q.is_emergency
      if (q.dateRange && q.dateRange.length === 2 && q.dateRange[0] && q.dateRange[1]) {
        p.start_date = q.dateRange[0]
        p.end_date = q.dateRange[1]
      }
      return p
    },
    async fetchList() {
      this.loading = true
      this.backendError = false
      try {
        const res = await repairApi.listOrders(this.buildParams())
        const data = res.data
        this.list = data.results || data
        this.total = data.count || (Array.isArray(data) ? data.length : 0)
      } catch (e) {
        // 后端不可用时保留空列表并标记，便于页面上给出明确提示（错误详情已由拦截器提示）
        this.list = []
        this.total = 0
        this.backendError = true
      } finally {
        this.loading = false
      }
    },
    // 点「查询」必须回到第 1 页，否则条件收窄后会请求越界页
    handleSearch() {
      this.currentPage = 1
      this.fetchList()
    },
    handleReset() {
      this.queryForm = {
        status: '',
        department: '',
        keyword: '',
        accept_by: '',
        is_emergency: '',
        dateRange: ['', ''],
      }
      this.currentPage = 1
      this.fetchList()
    },
    // 切换每页条数：回到第 1 页，防止按旧的深页码请求越界
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
      this.fetchList()
    },
    // 主列表序号：跨页连续编号（第 2 页从 11 开始，不重新起 1）
    mainIndex(index) {
      return (this.currentPage - 1) * this.pageSize + index + 1
    },
    // 优先级底色：急救/生命支持=淡红，被回退过的加急单=淡橙，两者同时满足红色优先
    rowClassName({ row }) {
      if (row.is_emergency) return 'row-emergency'
      if (row.is_urgent) return 'row-urgent'
      return ''
    },
    // ---------- 详情 ----------
    async openDetail(row) {
      this.detailVisible = true
      this.detailLoading = true
      this.detail = null
      try {
        const res = await repairApi.getOrderDetail(row.order_no)
        this.detail = res.data
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.detail) || '加载工单详情失败'
        this.$message.error(msg)
        this.detailVisible = false
      } finally {
        this.detailLoading = false
      }
    },
    // ---------- 导出维修记录单 PDF ----------
    // 后端返回 PDF 文件流（blob），浏览器侧转 ObjectURL 触发下载；
    // 写法与盘点确认单（InventoryList.downloadReport）保持一致。
    async exportReport() {
      if (!this.detail || !this.detail.order_no) return
      this.reportLoading = true
      try {
        const res = await repairApi.getReport(this.detail.order_no)
        const blob = new Blob([res.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `维修记录单_${this.detail.order_no}.pdf`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
        this.$message.success('维修记录单已导出')
      } catch (e) {
        // 失败时 response 是 blob，读不出 detail 文本，统一给通用提示
        const msg = typeof (e.response && e.response.data && e.response.data.detail) === 'string'
          ? e.response.data.detail
          : '导出失败，请检查后端服务'
        this.$message.error(msg)
      } finally {
        this.reportLoading = false
      }
    },
    // ---------- 派单 ----------
    async openDispatch(row) {
      this.dispatchOrderNo = row.order_no
      this.dispatchOpenid = ''
      this.dispatchVisible = true
      if (this.staffList.length) return
      this.staffLoading = true
      try {
        const res = await repairApi.listStaff()
        this.staffList = res.data || []
        if (!this.staffList.length) this.$message.warning('暂无可派单的后勤人员')
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.detail) || '后勤人员列表加载失败'
        this.$message.error(msg)
      } finally {
        this.staffLoading = false
      }
    },
    async submitDispatch() {
      if (!this.dispatchOpenid) {
        this.$message.warning('请选择要指派的后勤人员')
        return
      }
      this.dispatching = true
      try {
        await repairApi.dispatch(this.dispatchOrderNo, { target_openid: this.dispatchOpenid })
        this.$message.success('派单成功')
        this.dispatchVisible = false
        this.fetchList()
      } catch (e) {
        // 透出后端真实错误（如「仅待接单状态的工单可派单」）
        const msg = (e.response && e.response.data && e.response.data.detail) || '派单失败'
        this.$message.error(msg)
      } finally {
        this.dispatching = false
      }
    },
    // ---------- 导出 Excel ----------
    // 拉取与当前筛选一致的全量列表，建立 工单号→序号 映射（序号=在全量结果中的位置，与页面显示一致）
    // 后端 max_page_size=100，超过 100 条时循环翻页补齐；不抄这一段，导出第 2 页序号会从 1 重新开始
    async fetchSeqMap() {
      const map = {}
      try {
        const pageSize = 100
        let page = 1
        let total = Infinity
        while ((page - 1) * pageSize < total) {
          const params = { ...this.buildParams(), page, page_size: pageSize }
          const res = await repairApi.listOrders(params)
          const data = res.data
          const rows = data.results || data
          rows.forEach((row, i) => { map[row.order_no] = (page - 1) * pageSize + i + 1 })
          total = data.count || rows.length
          if (!rows.length) break
          page++
        }
      } catch (e) {
        // 拉取失败返回空映射，序号留空但不阻塞导出
      }
      return map
    },
    // 按当前筛选条件拉取全部匹配记录（不分页上限 100/页，循环翻页）
    async fetchAllRows() {
      const rows = []
      const pageSize = 100
      let page = 1
      let total = Infinity
      while ((page - 1) * pageSize < total) {
        const res = await repairApi.listOrders({ ...this.buildParams(), page, page_size: pageSize })
        const data = res.data
        const part = data.results || data
        rows.push(...part)
        total = data.count || part.length
        if (!part.length) break
        page++
      }
      return rows
    },
    async exportRows(rows, fileNamePrefix) {
      if (!rows || !rows.length) {
        this.$message.warning('没有可导出的记录')
        return
      }
      const seqMap = await this.fetchSeqMap()
      const headers = {
        seq: '序号',
        order_no: '工单号',
        equipment_name: '设备名称',
        equipment_model: '型号',
        equipment_factory_no: '出厂编号',
        equipment_manufacturer: '生产企业',
        department: '科室',
        reporter_name: '报修人',
        created_at: '报修时间',
        fault_desc: '故障描述',
        fault_cause: '故障原因',
        status: '状态',
        accept_by_name: '接单人',
        accepted_at: '接单时间',
        arrived_at: '到场时间',
        started_at: '开工时间',
        repair_unit: '维修单位',
        repair_person: '维修人',
        warranty_status: '保修状态',
        repair_result: '维修结果',
        test_result: '修复后检测',
        finished_at: '完毕日期',
        return_count: '回退次数',
        signed_by_name: '验收人',
        signed_at: '验收时间',
        parts: '配件明细',
      }
      const exportData = rows.map(row => {
        const item = {}
        Object.keys(headers).forEach(key => {
          let value = row[key]
          if (key === 'seq') value = seqMap[row.order_no] || ''
          else if (key === 'status') value = this.statusText(row)
          else if (key === 'repair_result') value = this.repairResultText(row)
          else if (key === 'repair_unit') value = this.repairUnitText(row)
          else if (key === 'warranty_status') value = this.warrantyText(row)
          else if (key === 'parts') value = this.partsText(row)
          item[headers[key]] = value !== undefined && value !== null ? value : ''
        })
        return item
      })
      const ws = XLSX.utils.json_to_sheet(exportData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '报修工单')
      XLSX.writeFile(wb, `${fileNamePrefix}_${this.formatDateForFile(new Date())}.xlsx`)
      this.$message.success(`已成功导出 ${rows.length} 条记录`)
    },
    async handleExportExcel() {
      // 有勾选 → 只导出勾选的工单；未勾选 → 导出当前筛选的全部
      const hasSelection = this.selectedRows.length > 0
      if (!hasSelection && !this.total) {
        this.$message.warning('当前没有可导出的工单')
        return
      }
      this.exporting = true
      try {
        const rows = hasSelection ? this.selectedRows.slice() : await this.fetchAllRows()
        await this.exportRows(rows, '报修工单')
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.detail) || '导出失败，请检查后端服务'
        this.$message.error(msg)
      } finally {
        this.exporting = false
      }
    },
    // 勾选变化（reserve-selection：翻页后勾选仍保留）
    onSelectionChange(rows) {
      this.selectedRows = rows
    },
    // 点复选框/全选框时不弹详情，只拦截 INPUT；其余区域点击照常打开详情
    onRowClick(row, column, event) {
      const tag = event && event.target && event.target.tagName
      if (tag === 'INPUT' || tag === 'LABEL') return
      this.openDetail(row)
    },
    formatDateForFile(d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const h = String(d.getHours()).padStart(2, '0')
      const min = String(d.getMinutes()).padStart(2, '0')
      const s = String(d.getSeconds()).padStart(2, '0')
      return `${y}${m}${day}_${h}${min}${s}`
    },
    // ---------- 急救设备批量打标 ----------
    openBatchFlag() {
      this.flagVisible = true
      this.flagRows = []
    },
    // 预览：按名称关键字逐个查台账（后端 name 为模糊匹配），合并去重
    async previewFlag() {
      const keywords = (this.flagKeywords || '')
        .split(/[,，;；\s]+/)
        .map(s => s.trim())
        .filter(Boolean)
      if (!keywords.length) {
        this.$message.warning('请输入至少一个设备名称关键字')
        return
      }
      this.flagLoading = true
      this.flagRows = []
      try {
        const seen = {}
        const rows = []
        for (const kw of keywords) {
          const res = await equipmentApi.list({ name: kw, page: 1, page_size: 100 })
          const data = res.data
          const part = data.results || data || []
          part.forEach(r => {
            if (seen[r.id]) return
            seen[r.id] = true
            rows.push(r)
          })
        }
        this.flagRows = rows
        if (!rows.length) this.$message.warning('没有匹配到设备，请调整关键字')
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.detail) || '设备台账查询失败'
        this.$message.error(msg)
      } finally {
        this.flagLoading = false
      }
    },
    // 执行打标：调用既有设备更新接口 PUT /equipment/{id}/，提交 { is_life_support: true }
    // 注意：equipment 表的 is_life_support 字段由后端 ALTER 提供（见 repair/sql/repair_table.sql），
    // 若后端序列化器尚未放开该字段，此处提交不会生效 —— 待后端确认设备更新接口支持该字段。
    async submitFlag() {
      const targets = this.flagRows.filter(r => !r.is_life_support)
      if (!targets.length) {
        this.$message.warning('预览清单中没有需要打标的设备')
        return
      }
      this.flagSubmitting = true
      let ok = 0
      let fail = 0
      let failMsg = ''
      for (const row of targets) {
        try {
          await equipmentApi.update(row.id, { is_life_support: true })
          row.is_life_support = true
          ok++
        } catch (e) {
          fail++
          const msg = (e.response && e.response.data && e.response.data.detail) || `设备 ${row.name} 打标失败`
          if (!failMsg) failMsg = msg
        }
      }
      this.flagSubmitting = false
      if (fail) {
        this.$message.error(`打标完成 ${ok} 台，失败 ${fail} 台：${failMsg}`)
      } else {
        this.$message.success(`已打标 ${ok} 台急救/生命支持类设备`)
      }
    },
    // ---------- 展示用工具方法 ----------
    statusText(row) {
      return (row && row.status_display) || STATUS_TEXT[row.status] || '-'
    },
    statusTagType(status) {
      const map = {
        PENDING: 'warning',
        PROCESSING: 'primary',
        SUSPENDED: 'info',
        WAIT_SIGN: 'warning',
        SIGNED: 'success',
        CANCELLED: 'info',
      }
      return map[status] || 'info'
    },
    repairResultText(row) {
      if (!row) return '-'
      return row.repair_result_display || RESULT_TEXT[row.repair_result] || '-'
    },
    repairUnitText(row) {
      if (!row) return '-'
      return row.repair_unit_name || REPAIR_UNIT_TEXT[row.repair_unit] || '-'
    },
    warrantyText(row) {
      if (!row) return '-'
      return WARRANTY_TEXT[row.warranty_status] || '-'
    },
    acceptSourceText(row) {
      if (!row) return '-'
      return ACCEPT_SOURCE_TEXT[row.accept_source] || row.accept_source || '-'
    },
    // 故障标签：后端可能给数组或逗号分隔字符串
    tagList(tags) {
      if (!tags) return []
      if (Array.isArray(tags)) return tags
      return String(tags).split(/[,，]/).map(s => s.trim()).filter(Boolean)
    },
    partsText(row) {
      const parts = (row && row.parts) || []
      if (!parts.length) return ''
      return parts.map(p => `${p.part_name || ''}${p.part_model ? '/' + p.part_model : ''} × ${p.quantity || 0}`).join('；')
    },
    partsTotal(detail) {
      const parts = (detail && detail.parts) || []
      const sum = parts.reduce((s, p) => s + (Number(p.amount) || 0), 0)
      return sum.toFixed(2)
    },
    mediaUrl(url) {
      if (!url) return ''
      if (/^https?:\/\//.test(url) || url.indexOf('/media/') === 0) return url
      return '/media/' + url
    },
  },
}
</script>

<style scoped>
.repair-page {
  padding: 16px;
}
.box-card {
  width: 100%;
}
.header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.page-title {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
}
.header-actions {
  display: flex;
  gap: 8px;
}
/* 查询区缩小一号：label 13px、输入框 13px/高 32px、间距收紧（与设备台账保持一致） */
.search-form /deep/ .el-form-item__label {
  font-weight: bold;
  font-size: 13px;
}
.search-form /deep/ .el-input__inner {
  font-size: 13px;
  height: 32px;
  line-height: 32px;
  color: #303133;
}
.search-form /deep/ .el-input__inner::placeholder {
  font-size: 13px;
}
.search-form /deep/ .el-input__icon {
  line-height: 32px;
}
.search-form /deep/ .el-range-editor.el-input__inner {
  padding: 0 8px;
}
.search-form /deep/ .el-date-editor .el-range-separator {
  line-height: 26px;
}
.search-form {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 10px 12px 0 12px;
}
.search-form .el-form-item {
  margin-bottom: 10px;
}
.search-buttons {
  margin-left: auto;
}
.legend-alert {
  margin-bottom: 12px;
}
.pager {
  margin-top: 16px;
  text-align: right;
}
.mark-tag {
  margin-left: 4px;
}
.overdue-text {
  color: #f56c6c;
  font-weight: bold;
  margin-left: 6px;
}
.return-count {
  color: #e6a23c;
  font-weight: bold;
}
.dispatch-text {
  color: #e6a23c;
}
/* 优先级底色：急救/生命支持=淡红，回退加急=淡橙，红色优先
   ⚠️ 同样要能压过「暖米白」底（App.vue 全局 #f6f1e7）：原来的 #fef0f0 / #fdf6ec
   在米白底上几乎同色，看起来像没生效，这里一并换成明显偏红/偏橙的两档。 */
.repair-table /deep/ .el-table__body tr.row-emergency > td {
  background-color: #fad7d5 !important;
}
.repair-table /deep/ .el-table__body tr.row-emergency > td:first-child {
  border-left: 3px solid #f56c6c;
}
.repair-table /deep/ .el-table__body tr.row-emergency:hover > td {
  background-color: #f6c6c3 !important;
}
.repair-table /deep/ .el-table__body tr.row-urgent > td {
  background-color: #fbe9c9 !important;
}
.repair-table /deep/ .el-table__body tr.row-urgent > td:first-child {
  border-left: 3px solid #e6a23c;
}
.repair-table /deep/ .el-table__body tr.row-urgent:hover > td {
  background-color: #f7dfb2 !important;
}
.repair-table /deep/ .el-table__body tr {
  cursor: pointer;
}
/* 详情弹窗 */
.detail-wrap {
  max-height: 68vh;
  overflow-y: auto;
  padding-right: 4px;
}
.detail-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.detail-order {
  font-size: 15px;
  font-weight: bold;
  color: #303133;
}
.detail-row {
  font-size: 13px;
  line-height: 1.9;
  color: #303133;
}
.detail-row .k {
  color: #909399;
}
.detail-row /deep/ .el-col {
  word-break: break-all;
}
.tag-gap {
  margin-right: 4px;
}
.parts-total {
  margin-top: 6px;
  text-align: right;
  font-size: 13px;
  color: #303133;
}
.detail-timeline {
  padding-left: 4px;
  margin-top: 6px;
}
.tl-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
.tl-desc {
  font-size: 13px;
  color: #606266;
  margin-top: 4px;
  white-space: pre-wrap;
}
.attach-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.attach-item {
  width: 150px;
}
.attach-image {
  width: 150px;
  height: 150px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}
.attach-audio {
  width: 150px;
  height: 40px;
}
.attach-link {
  color: #409eff;
}
.attach-empty {
  font-size: 13px;
  color: #909399;
}
.dispatch-form,
.flag-form {
  margin-top: 14px;
}
</style>

<style>
/* 报修列表：表头水平显示 + 表头居中（全局样式 + 唯一类名隔离，不影响其他表格） */
.repair-table.el-table th > .cell {
  white-space: nowrap !important;
  text-align: center !important;
}
</style>
